import { useCallback, useEffect, useRef, useState } from 'react'

// Hands-free voice conversation: while `active`, continuously listens for one utterance at a
// time (Web Speech API), fires `onUtterance` when the user pauses, then automatically starts
// listening again once told to (via the `active` flag toggling back on) — the caller is
// responsible for turning `active` off while the assistant is thinking/speaking so the mic never
// captures its own voice, then back on once it's done. This mirrors a real call's turn-taking
// without needing full-duplex audio.
export function useConversationLoop(active: boolean, onUtterance: (text: string) => void) {
  const [transcript, setTranscript] = useState('')
  const recRef = useRef<any>(null)
  const activeRef = useRef(active)
  activeRef.current = active
  const onUtteranceRef = useRef(onUtterance)
  onUtteranceRef.current = onUtterance

  const supported = typeof window !== 'undefined' && !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)

  const stop = useCallback(() => {
    try { recRef.current?.abort() } catch { /* */ }
    recRef.current = null
  }, [])

  const startCycle = useCallback(() => {
    if (!supported || !activeRef.current || recRef.current) return
    const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const rec = new Ctor()
    rec.lang = 'en-US'
    rec.continuous = false
    rec.interimResults = true
    let finalText = ''
    rec.onresult = (e: any) => {
      let interim = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const chunk = e.results[i][0].transcript
        if (e.results[i].isFinal) finalText += chunk
        else interim += chunk
      }
      setTranscript(finalText || interim)
    }
    rec.onend = () => {
      recRef.current = null
      const text = finalText.trim()
      setTranscript('')
      if (text) {
        onUtteranceRef.current(text)
      } else if (activeRef.current) {
        startCycle() // heard nothing that cycle — keep listening rather than going silent
      }
    }
    rec.onerror = () => { /* onend fires regardless and handles restart/cleanup */ }
    recRef.current = rec
    try { rec.start() } catch { recRef.current = null }
  }, [supported])

  useEffect(() => {
    if (active) startCycle()
    else stop()
    return stop
  }, [active, startCycle, stop])

  return { transcript, supported }
}
